/**
 * Inkscape often exports logos with a self-referencing clipPath
 * (`clip-path` on a group whose clipPath `<use>`s that same group).
 * Browsers then paint an empty image for <img>/CSS mask; RN SvgXml
 * can fail similarly. Strip clip paths and ensure a root fill when
 * the SVG has no explicit fill colours (default black paths).
 */

function has_explicit_fill_color(xml) {
    if (/fill\s*=\s*["'](?!none\b)[^"']+["']/i.test(xml))
        return true;
    // style="... fill:#xxx ..." but not fill-opacity / fill-rule
    if (/(?:^|[;\s{])fill\s*:\s*(?!none\b|opacity\b|rule\b)[^;}"'\s]+/i.test(xml))
        return true;
    return false;
}

export function sanitize_theme_logo_svg_xml(xml) {
    if (typeof xml !== 'string' || xml.indexOf('<svg') === -1)
        return xml;

    let out = xml
        .replace(/\sclip-path\s*=\s*("[^"]*"|'[^']*')/gi, '')
        .replace(/<clipPath\b[^>]*>[\s\S]*?<\/clipPath>/gi, '');

    if (!has_explicit_fill_color(out))
        out = out.replace(/<svg\b/i, '<svg fill="#000000"');

    return out;
}

function utf8_to_base64(str) {
    if (typeof TextEncoder !== 'undefined') {
        const bytes = new TextEncoder().encode(str);
        let binary = '';
        bytes.forEach((b) => { binary += String.fromCharCode(b); });
        return btoa(binary);
    }
    return btoa(unescape(encodeURIComponent(str)));
}

function base64_to_utf8(b64) {
    if (typeof TextDecoder !== 'undefined') {
        const binary = atob(b64);
        const bytes = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i++)
            bytes[i] = binary.charCodeAt(i);
        return new TextDecoder().decode(bytes);
    }
    return decodeURIComponent(escape(atob(b64)));
}

/**
 * @param {string|null|undefined} dataUrl
 * @returns {string|null|undefined}
 */
export function sanitize_theme_logo_data_uri(dataUrl) {
    if (!dataUrl || typeof dataUrl !== 'string')
        return dataUrl;
    if (!/^data:image\/svg\+xml/i.test(dataUrl))
        return dataUrl;

    const base64_match = dataUrl.match(/^data:image\/svg\+xml;base64,(.+)$/i);
    if (base64_match) {
        const xml = base64_to_utf8(base64_match[1]);
        const sanitized = sanitize_theme_logo_svg_xml(xml);
        if (sanitized === xml)
            return dataUrl;
        return `data:image/svg+xml;base64,${utf8_to_base64(sanitized)}`;
    }

    const comma = dataUrl.indexOf(',');
    if (comma === -1)
        return dataUrl;
    const meta = dataUrl.slice(0, comma);
    const payload = dataUrl.slice(comma + 1);
    const xml = decodeURIComponent(payload);
    const sanitized = sanitize_theme_logo_svg_xml(xml);
    if (sanitized === xml)
        return dataUrl;
    return `${meta},${encodeURIComponent(sanitized)}`;
}
